import { Url } from '../../../../common';

export enum LifecycleState {
    published = 'PUBLISHED',
}

export enum MemberNetworkVisibility {
    connections = 'CONNECTIONS',
    public = 'PUBLIC',
    loggedIn = 'LOGGED_IN',
}

type Media = {
    id: string;
    title?: string;
    altText?: string;
};

type SingleImage = {
    media: Media;
};

type MultiImage = {
    multiImage: {
        images: Media[];
    };
};

type Content = MultiImage | SingleImage;

type Distribution = {
    feedDistribution: string;
    thirdPartyDistributionChannels?: string[];
};
export interface LinkedInPublishablePost {
    author: string;
    content: Content;
    commentary: string;
    distribution: Distribution;
    lifecycleState: LifecycleState;
    visibility: MemberNetworkVisibility;
}

export interface InitializeUploadRequest {
    initializeUploadRequest: InitializeUploadRequestPayload;
}

export interface InitializeUploadRequestPayload {
    owner: string;
}

export interface InitializeUploadResponse {
    value: InitializeUploadResponsePayload;
}

export interface InitializeUploadResponsePayload {
    uploadUrlExpiresAt: number;
    uploadUrl: Url;
    image: string;
}

/**
 * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/programmatic-refresh-tokens?context=linkedin/marketing/context#step-2-exchanging-a-refresh-token-for-a-new-access-token
 */
export interface ExchangeRefreshTokenForAccessTokenRequestBody {
    grant_type: 'refresh_token';

    /**
     * The refresh token to exchange for an access token
     */

    refresh_token: string;

    /**
     * The Client ID value generated when you registered your application
     */

    client_id: string;

    /**
     * The Client Secret value generated when you registered your application
     */

    client_secret: string;
}

/**
 * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/programmatic-refresh-tokens?context=linkedin/marketing/context#sample-request
 */
export interface ExchangeRefreshTokenForAccessTokenResponse {
    /**
     * The new access token
     */

    access_token: string;

    /**
     * Epoch time in seconds, indicating when this token will expire
     */

    expires_in: number;

    /**
     * The refresh token that was used to exchange for an access token
     */

    refresh_token: string;

    /**
     * Epoch time in seconds, indicating when this refresh token will expire
     */

    refresh_token_expires_in: number;
}

/**
 * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/token-introspection?context=linkedin/context#request-body-fields
 */
export interface IntrospectTokenRequestBody {
    /**
     * Application client Id
     */

    client_id: string;

    /**
     * Application client secret
     */

    client_secret: string;

    /**
     * The string value of the token returned from endpoints Client Credential Flow (2-legged OAuth) & Authorization
     * Code Flow (3-legged OAuth)
     *
     * @see {@link https://docs.microsoft.com/en-us/linkedin/shared/authentication/client-credentials-flow Client Credential Flow}
     * @see {@link https://docs.microsoft.com/en-us/linkedin/shared/authentication/authorization-code-flow Authorization Flow}
     */
    token: string;
}

export type IntrospectTokenNotFoundResponse = {
    active: false;
    status: undefined;
};

type IntrospectTokenBaseResponse = {
    /**
     * Epoch time in seconds, indicating when this token was originally issued
     *
     * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/token-introspection?context=linkedin/context#response-body-fields
     */

    created_at: number;
    /**
     * Epoch time in seconds, indicating when this token will expire
     *
     * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/token-introspection?context=linkedin/context#response-body-fields
     */

    expires_in: number;
    /**
     * A string containing a comma-separated list of scopes associated with this token. Returned only for token
     * obtained via Authorization Code Flow (3-legged OAuth)
     *
     * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/token-introspection?context=linkedin/context#response-body-fields
     */
    scope?: string;
    /**
     * Application client Id
     *
     * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/token-introspection?context=linkedin/context#response-body-fields
     */

    client_id: string;
};

export type IntrospectTokenActiveResponse = IntrospectTokenBaseResponse & {
    active: true;
    status: 'active';
    /**
     * Epoch time in seconds, indicating when the token was authorized
     *
     * @see https://docs.microsoft.com/en-us/linkedin/shared/authentication/token-introspection?context=linkedin/context#response-body-fields
     */

    authorized_at: number;
};
export type IntrospectTokenExpiredResponse = IntrospectTokenBaseResponse & {
    active: false;
    status: 'expired';
};
export type IntrospectTokenRevokedResponse = IntrospectTokenBaseResponse & {
    active: false;
    status: 'revoked';
};

export type IntrospectTokenResponse =
    | IntrospectTokenActiveResponse
    | IntrospectTokenExpiredResponse
    | IntrospectTokenNotFoundResponse
    | IntrospectTokenRevokedResponse;
